/**
 * fxTier — orçamento central de efeitos de interface.
 *
 * Um único lugar decide quanto a UI pode gastar em blur, glow e
 * animação; o resultado vai para `data-fx` / `data-motion` no <html>
 * e os tokens de `index.css` reagem. Componentes não precisam saber
 * de qualidade de gráficos nem de FPS — quem precisa de decisão em JS
 * (contagem de partículas, montar ou não uma camada) usa `useFx()`.
 *
 * Entradas:
 *   - useGraphics.quality  (preset escolhido/auto-detectado)
 *   - usePerformance.averageFps (rebaixa em tempo real se o menu engasgar)
 *   - prefers-reduced-motion   (preferência do SO)
 *   - toggle manual de movimento reduzido (persistido)
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";
import { useGraphics, type GraphicsQuality } from "@/lib/stores/useGraphics";
import { usePerformance } from "@/lib/stores/usePerformance";

export type FxTier = "low" | "medium" | "high";

/** Abaixo disto o menu já está engasgando e rebaixamos o tier. */
const FPS_DEMOTE_THRESHOLD = 40;
/** Acima disto podemos devolver o tier original. */
const FPS_PROMOTE_THRESHOLD = 52;
/** Quantas amostras seguidas antes de mexer — evita oscilar a cada frame ruim. */
const SAMPLES_BEFORE_CHANGE = 3;

interface FxState {
  /** Tier efetivo já considerando rebaixamento por FPS. */
  tier: FxTier;
  /** Movimento reduzido efetivo (SO OU toggle manual). */
  reducedMotion: boolean;
  /** Toggle manual, independente da preferência do SO. */
  motionPreference: "auto" | "reduced" | "full";
  setMotionPreference: (pref: "auto" | "reduced" | "full") => void;
  /** Escrito pelo controlador; não chamar de componentes. */
  _apply: (tier: FxTier, reducedMotion: boolean) => void;
}

export const useFx = create<FxState>()(
  persist(
    (set) => ({
      tier: "high",
      reducedMotion: false,
      motionPreference: "auto",
      setMotionPreference: (motionPreference) => {
        set({ motionPreference });
        syncFxTier();
      },
      _apply: (tier, reducedMotion) => set({ tier, reducedMotion }),
    }),
    {
      name: "rocketanks-fx",
      partialize: (state) => ({ motionPreference: state.motionPreference }),
    },
  ),
);

function prefersReducedMotionOS(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/** Rebaixamento temporário decidido pelo watcher de FPS. */
let fpsPenalty = 0;
let lowSamples = 0;
let highSamples = 0;

const TIER_ORDER: FxTier[] = ["low", "medium", "high"];

function resolveTier(quality: GraphicsQuality): FxTier {
  const baseIndex = TIER_ORDER.indexOf(quality as FxTier);
  const index = Math.max(0, (baseIndex === -1 ? 2 : baseIndex) - fpsPenalty);
  return TIER_ORDER[index];
}

function resolveReducedMotion(): boolean {
  const pref = useFx.getState().motionPreference;
  if (pref === "reduced") return true;
  if (pref === "full") return false;
  return prefersReducedMotionOS();
}

/** Recalcula e escreve no DOM + store. Idempotente e barato. */
export function syncFxTier(): void {
  if (typeof document === "undefined") return;

  const tier = resolveTier(useGraphics.getState().quality);
  const reducedMotion = resolveReducedMotion();
  const root = document.documentElement;

  if (root.dataset.fx !== tier) root.dataset.fx = tier;

  const motionValue = reducedMotion ? "reduced" : "full";
  if (root.dataset.motion !== motionValue) root.dataset.motion = motionValue;

  const current = useFx.getState();
  if (current.tier !== tier || current.reducedMotion !== reducedMotion) {
    current._apply(tier, reducedMotion);
  }
}

let initialized = false;
let disposers: Array<() => void> = [];

/**
 * Liga o controlador. Chamar uma vez no boot do app.
 * Retorna um disposer (usado só em testes/HMR).
 */
export function initFxTier(): () => void {
  if (initialized) return () => {};
  initialized = true;

  syncFxTier();

  // Preset de gráficos mudou (usuário ou auto-detect)
  disposers.push(
    useGraphics.subscribe((state, prev) => {
      if (state.quality !== prev.quality) {
        // Preset novo zera a penalidade — o usuário pediu explicitamente.
        fpsPenalty = 0;
        lowSamples = 0;
        highSamples = 0;
        syncFxTier();
      }
    }),
  );

  // FPS caiu de forma sustentada → rebaixa; recuperou → devolve.
  disposers.push(
    usePerformance.subscribe((state, prev) => {
      if (state.averageFps === prev.averageFps) return;
      const fps = state.averageFps;

      if (fps < FPS_DEMOTE_THRESHOLD) {
        highSamples = 0;
        lowSamples += 1;
        if (lowSamples >= SAMPLES_BEFORE_CHANGE && fpsPenalty < 2) {
          fpsPenalty += 1;
          lowSamples = 0;
          syncFxTier();
        }
      } else if (fps > FPS_PROMOTE_THRESHOLD) {
        lowSamples = 0;
        highSamples += 1;
        if (highSamples >= SAMPLES_BEFORE_CHANGE && fpsPenalty > 0) {
          fpsPenalty -= 1;
          highSamples = 0;
          syncFxTier();
        }
      } else {
        lowSamples = 0;
        highSamples = 0;
      }
    }),
  );

  // Preferência de movimento do SO
  if (typeof window !== "undefined" && window.matchMedia) {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => syncFxTier();
    if (mq.addEventListener) {
      mq.addEventListener("change", onChange);
      disposers.push(() => mq.removeEventListener("change", onChange));
    } else if (mq.addListener) {
      // Safari < 14 / WebViews antigas
      mq.addListener(onChange);
      disposers.push(() => mq.removeListener(onChange));
    }
  }

  return () => {
    disposers.forEach((d) => d());
    disposers = [];
    initialized = false;
  };
}

/** Helpers para decisões em JS (contagem de partículas, montar camada…). */
export function fxAtLeast(tier: FxTier): boolean {
  return TIER_ORDER.indexOf(useFx.getState().tier) >= TIER_ORDER.indexOf(tier);
}

/** Escolhe um valor por tier sem espalhar `if` pelos componentes. */
export function byTier<T>(values: { low: T; medium: T; high: T }): T {
  return values[useFx.getState().tier];
}
